/**
 * Cookie consent banner — Google Consent Mode v2 surface.
 * Shown only when the user has not yet made a choice.
 * Uses shadcn primitives. No third-party deps.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  acceptAllConsent,
  hasUserDecided,
  rejectAllConsent,
} from "@/lib/consent";
import { setConsentBannerVisible } from "@/hooks/useConsentBannerVisible";

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer banner until the browser is idle AND after first paint, so it
    // never becomes the LCP element or blocks initial render. GDPR is
    // unaffected — no tracking fires until the user makes a choice.
    let cancelled = false;
    const show = () => { if (!cancelled) setVisible(!hasUserDecided()); };
    const schedule = () => {
      if ("requestIdleCallback" in window) {
        (window as any).requestIdleCallback(show, { timeout: 4000 });
      } else {
        setTimeout(show, 2500);
      }
    };
    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
    return () => { cancelled = true; };
  }, []);

  // Broadcast visibility so floating surfaces (Consultant Assistant FAB) can
  // stand down while the consent controls are on screen.
  useEffect(() => {
    setConsentBannerVisible(visible);
    return () => setConsentBannerVisible(false);
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => setVisible(false);

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[70] border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-lg"
    >

      <div className="container mx-auto flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground max-w-3xl">
          We use cookies to improve your experience, measure performance and personalise marketing.
          You can accept all, reject non-essential, or read our{" "}
          <a href="/privacy-policy" className="underline hover:text-foreground">
            privacy policy
          </a>
          . Essential cookies that keep the site working are always on.
        </p>
        <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              rejectAllConsent();
              dismiss();
            }}
          >
            Reject non-essential
          </Button>
          <Button
            size="sm"
            onClick={() => {
              acceptAllConsent();
              dismiss();
            }}
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
